/** Pure helpers for the person's own main rail order, shared by the rail, the number shortcuts and the Preferences list. */

/** Moves one entry to a new index without mutating; out-of-range indexes clamp to the ends. */
export function moveItem<T>(order: readonly T[], from: number, to: number): T[] {
  const next = [...order];
  if (from < 0 || from >= next.length) return next;
  const target = Math.max(0, Math.min(next.length - 1, to));
  const [moved] = next.splice(from, 1);
  next.splice(target, 0, moved);
  return next;
}

/** Lays the stored order over the definitions: unknown ids are dropped and ids the order has never seen follow in definition order. */
export function applyRailOrder<T extends { id: string }>(definitions: readonly T[], order: readonly string[]): T[] {
  if (order.length === 0) return [...definitions];
  const byId = new Map(definitions.map((definition) => [definition.id, definition] as const));
  const placed = new Set<string>();
  const ordered: T[] = [];
  for (const id of order) {
    const definition = byId.get(id);
    if (!definition || placed.has(id)) continue;
    placed.add(id);
    ordered.push(definition);
  }
  for (const definition of definitions) {
    if (!placed.has(definition.id)) ordered.push(definition);
  }
  return ordered;
}

/** Groups render one after another (main, CRM, admin), so the saved order lists them the same way: each group keeps its own members in the person's order. */
export function orderByGroup<T extends { id: string }>(groups: readonly (readonly T[])[], order: readonly string[]): T[] {
  return groups.flatMap((group) => applyRailOrder(group, order));
}

/** Like moveItem, but an entry never leaves its group: a target in another group leaves the order as it was. */
export function moveItemWithinGroup(
  order: readonly string[],
  from: number,
  to: number,
  groupOf: (id: string) => string | undefined,
): string[] {
  const target = Math.max(0, Math.min(order.length - 1, to));
  if (from < 0 || from >= order.length) return [...order];
  if (groupOf(order[from]) !== groupOf(order[target])) return [...order];
  return moveItem(order, from, target);
}

/** True when a one-step move would cross into a neighbouring group, or off either end. */
export function isGroupEdge(
  order: readonly string[],
  index: number,
  delta: -1 | 1,
  groupOf: (id: string) => string | undefined,
): boolean {
  const neighbour = order[index + delta];
  return neighbour === undefined || groupOf(neighbour) !== groupOf(order[index]);
}
